import { PropertyRecord } from '@app/models/property.model';
import {
  ListingStatusFilterKey,
  resolveListingStatusFilterKey,
} from '@app/utils/listing-status.util';

export interface ResidentialFilterState {
  minPrice: number;
  maxPrice: number;
  listOfBathMinOptions: number[];
  listOfBathMaxOptions: number[];
  minBaths: number;
  maxBaths: number;
  bedsMin: number;
  lotSizeMin: number;
  lotSizeMax: number;
  sqftMin: number;
  sqftMax: number;
  pricePerSqFtMin: number;
  pricePerSqFtMax: number;
  maxDaysOnMarket: number;
  yearBuiltMin: number;
  yearBuiltMax: number;
  requireVirtualTour: boolean;
  poolFilter: YesNoFilter;
  fireplaceFilter: YesNoFilter;
  requireGarage: boolean;
  includeRentals: boolean;
}

export const DEFAULT_LOT_SIZE_MAX = 1_000_000;
export const DEFAULT_SQFT_MAX = 50_000;
export const DEFAULT_PRICE_PER_SQFT_MAX = 10_000;
export const DEFAULT_YEAR_BUILT_MAX = 2100;

export type YesNoFilter = 'any' | 'yes' | 'no';

export const YES_NO_SEGMENT_OPTIONS: readonly { id: YesNoFilter; label: string }[] = [
  { id: 'any', label: 'Any' },
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
];

export interface SchoolDistrictFilterOption {
  value: string;
  label: string;
}

export interface StoriesFilterOption {
  value: string;
  label: string;
}

/** Common LBR school district values (RETS LM_Char10_7). */
export const SCHOOL_DISTRICT_FILTER_OPTIONS: readonly SchoolDistrictFilterOption[] = [
  { value: 'UISD', label: 'United ISD (UISD)' },
  { value: 'LISD', label: 'Laredo ISD (LISD)' },
];

/** Common LBR story/style values (RETS LFD_STYLE_1). */
export const STORIES_FILTER_OPTIONS: readonly StoriesFilterOption[] = [
  { value: '1 Story', label: '1 story' },
  { value: '2 Story', label: '2 story' },
  { value: '3 Story', label: '3 story' },
  { value: 'Multi-Level', label: 'Multi-level' },
];

export const DAYS_ON_MARKET_FILTER_OPTIONS = [
  { id: 'dom-any', maxDays: 0, label: 'Any' },
  { id: 'dom-7', maxDays: 7, label: '7 days' },
  { id: 'dom-14', maxDays: 14, label: '14 days' },
  { id: 'dom-30', maxDays: 30, label: '30 days' },
  { id: 'dom-90', maxDays: 90, label: '90 days' },
] as const;

export interface CommercialFilterState {
  sizeMin: number;
  sizeMax: number;
}

export interface LandFilterState {
  areaMin: number;
  areaMax: number;
}

export interface TypesToDisplayState {
  residentialProperties: boolean;
  commercialProperties: boolean;
  industrialProperties: boolean;
  landProperties: boolean;
}

export type ListingTypeFilter = 'all' | 'residential' | 'commercial' | 'industrial' | 'land';

export interface MlsPropertyTypeOption {
  value: string;
  label: string;
}

export interface MlsPropertyTypeOptionGroup {
  category: Exclude<ListingTypeFilter, 'all'>;
  label: string;
  options: readonly MlsPropertyTypeOption[];
}

export const LISTING_CATEGORY_LABELS: Record<Exclude<ListingTypeFilter, 'all'>, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  land: 'Land',
  industrial: 'Industrial',
};

export const MLS_PROPERTY_TYPE_GROUP_ORDER: readonly Exclude<ListingTypeFilter, 'all'>[] = [
  'residential',
  'commercial',
  'land',
  'industrial',
];

/** MLS `L_Type_` values grouped by listing category (verified against LBR RETS). */
export const MLS_PROPERTY_TYPES_BY_CATEGORY: Record<
  Exclude<ListingTypeFilter, 'all'>,
  readonly MlsPropertyTypeOption[]
> = {
  residential: [
    { value: 'Single Family Residential', label: 'Houses' },
    { value: 'Condo/Townhouse', label: 'Condos & townhomes' },
    { value: 'Apartment', label: 'Apartments' },
    { value: 'Mobile', label: 'Mobile / manufactured' },
    { value: 'Duplex', label: 'Duplex' },
  ],
  land: [
    { value: 'Ranch Land', label: 'Ranch land' },
    { value: 'Commercial Land', label: 'Commercial land' },
    { value: 'Residential Land', label: 'Residential land' },
    { value: 'Industrial Land', label: 'Industrial land' },
  ],
  industrial: [
    { value: 'Warehouse', label: 'Warehouse' },
    { value: 'Industrial', label: 'Industrial' },
  ],
  commercial: [
    { value: 'Retail', label: 'Retail' },
    { value: 'Office', label: 'Office' },
    { value: 'Hospitality', label: 'Hospitality' },
  ],
};

export function getMlsPropertyTypeOptionGroups(
  category: ListingTypeFilter,
): MlsPropertyTypeOptionGroup[] {
  const categories = category === 'all'
    ? MLS_PROPERTY_TYPE_GROUP_ORDER
    : [category];

  return categories.map((listingCategory) => ({
    category: listingCategory,
    label: LISTING_CATEGORY_LABELS[listingCategory],
    options: MLS_PROPERTY_TYPES_BY_CATEGORY[listingCategory],
  }));
}

export function getMlsPropertyTypeOptions(category: ListingTypeFilter): MlsPropertyTypeOption[] {
  return getMlsPropertyTypeOptionGroups(category).flatMap((group) => [...group.options]);
}

export function matchesMlsPropertyTypeFilter(
  property: PropertyRecord,
  selectedTypes: ReadonlySet<string>,
): boolean {
  if (selectedTypes.size === 0) {
    return true;
  }

  const mlsType = String(property.MlsPropertyType ?? '').trim();
  if (!mlsType) {
    return false;
  }

  return selectedTypes.has(mlsType);
}

export function formatMlsPropertyTypeFilterLabel(
  selectedTypes: ReadonlySet<string>,
  category: ListingTypeFilter,
): string {
  if (selectedTypes.size === 0) {
    return 'Property type';
  }

  const labelsByValue = new Map(
    getMlsPropertyTypeOptions(category).map((option) => [option.value, option.label]),
  );
  const labels = [...selectedTypes]
    .map((value) => labelsByValue.get(value) ?? value)
    .sort((left, right) => left.localeCompare(right));

  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length === 2) {
    return `${labels[0]}, ${labels[1]}`;
  }
  return `${labels[0]} +${labels.length - 1}`;
}

export function parseFilterNumber(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function getBedMinFromFilterId(filterId: string): number {
  if (filterId === 'bed-filter-one') return 1;
  if (filterId === 'bed-filter-two') return 2;
  if (filterId === 'bed-filter-three') return 3;
  if (filterId === 'bed-filter-four') return 4;
  if (filterId === 'bed-filter-five') return 5;
  return 0;
}

export function getBathMinFromFilterId(filterId: string): number {
  if (filterId === 'bath-filter-one') return 1;
  if (filterId === 'bath-filter-two') return 2;
  if (filterId === 'bath-filter-three') return 3;
  if (filterId === 'bath-filter-four') return 4;
  return 0;
}

export function getCurrentTypeFilter(types: TypesToDisplayState): ListingTypeFilter {
  if (
    types.residentialProperties &&
    types.commercialProperties &&
    types.industrialProperties &&
    types.landProperties
  ) {
    return 'all';
  }
  if (types.residentialProperties) return 'residential';
  if (types.commercialProperties) return 'commercial';
  if (types.industrialProperties) return 'industrial';
  if (types.landProperties) return 'land';
  return 'all';
}

export function applyTypeFilterValue(
  types: TypesToDisplayState,
  value: string,
): TypesToDisplayState {
  if (value === 'residential') {
    return {
      residentialProperties: true,
      commercialProperties: false,
      industrialProperties: false,
      landProperties: false,
    };
  }
  if (value === 'commercial') {
    return {
      residentialProperties: false,
      commercialProperties: true,
      industrialProperties: false,
      landProperties: false,
    };
  }
  if (value === 'industrial') {
    return {
      residentialProperties: false,
      commercialProperties: false,
      industrialProperties: true,
      landProperties: false,
    };
  }
  if (value === 'land') {
    return {
      residentialProperties: false,
      commercialProperties: false,
      industrialProperties: false,
      landProperties: true,
    };
  }
  return {
    residentialProperties: true,
    commercialProperties: true,
    industrialProperties: true,
    landProperties: true,
  };
}

export interface ListingStatusFilterOption {
  value: ListingStatusFilterKey;
  label: string;
  count: number;
}

const LISTING_STATUS_FILTER_DEFINITIONS: readonly {
  value: ListingStatusFilterKey;
  label: string;
}[] = [
  { value: 'active', label: 'Active' },
  { value: 'contingent', label: 'Contingent' },
  { value: 'pending', label: 'Pending' },
  { value: 'back-on-market', label: 'Back on market' },
  { value: 'extended', label: 'Extended' },
];

/** Static listing-status checkbox options (no live facet counts). */
export const LISTING_STATUS_FILTER_OPTIONS = LISTING_STATUS_FILTER_DEFINITIONS;

const LEGACY_LISTING_STATUS_FILTER_ALIASES: Record<string, ListingStatusFilterKey> = {
  active: 'active',
  pending: 'pending',
  contingent: 'contingent',
  'active contingent': 'contingent',
  'active under contract': 'pending',
  'coming soon': 'other',
  'back on market': 'back-on-market',
  extended: 'extended',
};

export function normalizeListingStatusFilterValue(value: string): ListingStatusFilterKey | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const lower = trimmed.toLowerCase();
  if (lower in LEGACY_LISTING_STATUS_FILTER_ALIASES) {
    return LEGACY_LISTING_STATUS_FILTER_ALIASES[lower];
  }

  const slug = lower.replace(/\s+/g, '-');
  if (LISTING_STATUS_FILTER_DEFINITIONS.some((option) => option.value === slug)) {
    return slug as ListingStatusFilterKey;
  }

  return null;
}

export function buildListingStatusFilterOptions(
  properties: readonly PropertyRecord[],
): ListingStatusFilterOption[] {
  const counts = new Map<ListingStatusFilterKey, number>();

  for (const property of properties) {
    const key = resolveListingStatusFilterKey(property.Status);
    if (!key || key === 'other') {
      continue;
    }
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return LISTING_STATUS_FILTER_DEFINITIONS
    .filter((definition) => (counts.get(definition.value) ?? 0) > 0)
    .map((definition) => {
      const count = counts.get(definition.value) ?? 0;
      return {
        value: definition.value,
        label: definition.label,
        count,
      };
    });
}

export function matchesListingStatusFilter(
  property: PropertyRecord,
  selectedStatuses: ReadonlySet<string>,
): boolean {
  if (selectedStatuses.size === 0) {
    return true;
  }

  const key = resolveListingStatusFilterKey(property.Status);
  if (!key) {
    return false;
  }

  const normalizedSelected = new Set(
    [...selectedStatuses]
      .map((selected) => normalizeListingStatusFilterValue(selected))
      .filter((selected): selected is ListingStatusFilterKey => selected !== null),
  );

  return normalizedSelected.has(key);
}

export function formatListingStatusFilterLabel(selectedStatuses: ReadonlySet<string>): string {
  if (selectedStatuses.size === 0) {
    return '';
  }

  const labelsByValue = new Map<string, string>(
    LISTING_STATUS_FILTER_DEFINITIONS.map((option) => [option.value, option.label]),
  );
  const labels = [...selectedStatuses]
    .map((value) => labelsByValue.get(value) ?? value)
    .sort((left, right) => left.localeCompare(right));

  return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`;
}

export function matchesSchoolDistrictFilter(
  property: PropertyRecord,
  selectedDistricts: ReadonlySet<string>,
): boolean {
  if (selectedDistricts.size === 0) {
    return true;
  }

  const district = String(property.SchoolDistrict ?? '').trim();
  if (!district) {
    return false;
  }

  return selectedDistricts.has(district);
}

export function matchesStoriesFilter(
  property: PropertyRecord,
  selectedStories: ReadonlySet<string>,
): boolean {
  if (selectedStories.size === 0) {
    return true;
  }

  const stories = String(property.Stories ?? '').trim();
  if (!stories) {
    return false;
  }

  return selectedStories.has(stories);
}

export function parseYesNoFilter(value: string | null | undefined): YesNoFilter {
  if (value === 'yes' || value === 'no') {
    return value;
  }
  return 'any';
}

/** Treats MLS yes/no and descriptive values (e.g. garage type) as present when meaningful. */
export function mlsFieldHasPositiveValue(value: unknown): boolean {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) {
    return false;
  }
  if (text === 'no' || text === 'none' || text === 'n' || text === 'false' || text === '0') {
    return false;
  }
  if (text === 'yes' || text === 'y' || text === 'true') {
    return true;
  }

  const num = Number(text);
  if (Number.isFinite(num)) {
    return num > 0;
  }

  return true;
}

export function matchesYesNoFieldFilter(value: unknown, filter: YesNoFilter): boolean {
  if (filter === 'any') {
    return true;
  }

  const hasValue = mlsFieldHasPositiveValue(value);
  return filter === 'yes' ? hasValue : !hasValue;
}

export function hasVirtualTour(value: unknown): boolean {
  return Boolean(String(value ?? '').trim());
}

function startOfLocalDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function parseListingDate(value: string): Date | null {
  const dateOnly = value.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const [year, month, day] = dateOnly.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return startOfLocalDay(new Date(parsed));
}

export function resolveDaysOnMarket(
  property: PropertyRecord,
  referenceDate = new Date(),
): number | null {
  const stored = Number(property.DaysOnMarket);
  if (Number.isFinite(stored) && stored >= 0) {
    return stored;
  }

  const listedDateText = String(property.ListedDate ?? '').trim();
  if (!listedDateText) {
    return null;
  }

  const listedDay = parseListingDate(listedDateText);
  if (!listedDay) {
    return null;
  }

  const referenceDay = startOfLocalDay(referenceDate);
  const diffMs = referenceDay.getTime() - listedDay.getTime();
  if (diffMs < 0) {
    return 0;
  }

  return Math.floor(diffMs / 86_400_000);
}

export function getDaysOnMarketFilterId(maxDays: number): string {
  const match = DAYS_ON_MARKET_FILTER_OPTIONS.find((option) => option.maxDays === maxDays);
  return match?.id ?? 'dom-any';
}

export function getMaxDaysFromFilterId(filterId: string): number {
  const match = DAYS_ON_MARKET_FILTER_OPTIONS.find((option) => option.id === filterId);
  return match?.maxDays ?? 0;
}

export function formatSchoolDistrictFilterLabel(selectedDistricts: ReadonlySet<string>): string {
  if (selectedDistricts.size === 0) {
    return '';
  }

  const labelsByValue = new Map(
    SCHOOL_DISTRICT_FILTER_OPTIONS.map((option) => [option.value, option.label]),
  );
  const labels = [...selectedDistricts]
    .map((value) => labelsByValue.get(value) ?? value)
    .sort((left, right) => left.localeCompare(right));

  return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`;
}

export function formatStoriesFilterLabel(selectedStories: ReadonlySet<string>): string {
  if (selectedStories.size === 0) {
    return '';
  }

  const labelsByValue = new Map(STORIES_FILTER_OPTIONS.map((option) => [option.value, option.label]));
  const labels = [...selectedStories]
    .map((value) => labelsByValue.get(value) ?? value)
    .sort((left, right) => left.localeCompare(right));

  return labels.length === 1 ? labels[0] : `${labels[0]} +${labels.length - 1}`;
}

export function isRentalListing(property: PropertyRecord): boolean {
  const saleRent = String(property.SaleRent ?? property['SaleRent'] ?? '').trim().toLowerCase();
  if (!saleRent) {
    return false;
  }
  return saleRent.includes('rent') || saleRent.includes('lease');
}

export function matchesSaleRentFilter(
  property: PropertyRecord,
  includeRentals: boolean,
): boolean {
  if (includeRentals) {
    return true;
  }
  return !isRentalListing(property);
}

export function matchesResidentialFilters(
  property: PropertyRecord,
  filters: ResidentialFilterState,
): boolean {
  const lotSize = Number(property.LotSizeSqFt ?? 0);
  const livingSqft = Number(property.Area ?? 0);
  const pricePerSqFt = Number(property.PricePerSqFt ?? 0);
  const daysOnMarket = resolveDaysOnMarket(property);

  if (filters.sqftMin > 0 && (!livingSqft || livingSqft < filters.sqftMin)) {
    return false;
  }
  if (filters.sqftMax < DEFAULT_SQFT_MAX && livingSqft > filters.sqftMax) {
    return false;
  }
  if (filters.lotSizeMin > 0 && (!lotSize || lotSize < filters.lotSizeMin)) {
    return false;
  }
  if (filters.lotSizeMax < DEFAULT_LOT_SIZE_MAX && lotSize > filters.lotSizeMax) {
    return false;
  }
  if (filters.pricePerSqFtMin > 0 && (!pricePerSqFt || pricePerSqFt < filters.pricePerSqFtMin)) {
    return false;
  }
  if (
    filters.pricePerSqFtMax < DEFAULT_PRICE_PER_SQFT_MAX
    && pricePerSqFt > filters.pricePerSqFtMax
  ) {
    return false;
  }
  if (
    filters.maxDaysOnMarket > 0
    && (daysOnMarket === null || daysOnMarket > filters.maxDaysOnMarket)
  ) {
    return false;
  }

  const yearBuilt = Number(property.YearBuilt ?? 0);
  if (filters.yearBuiltMin > 0 && (!yearBuilt || yearBuilt < filters.yearBuiltMin)) {
    return false;
  }
  if (filters.yearBuiltMax < DEFAULT_YEAR_BUILT_MAX && yearBuilt > filters.yearBuiltMax) {
    return false;
  }
  if (filters.requireVirtualTour && !hasVirtualTour(property.VirtualTourUrl)) {
    return false;
  }
  if (!matchesYesNoFieldFilter(property.Pool, filters.poolFilter)) {
    return false;
  }
  if (!matchesYesNoFieldFilter(property.Fireplaces, filters.fireplaceFilter)) {
    return false;
  }
  if (filters.requireGarage && !mlsFieldHasPositiveValue(property.Garage)) {
    return false;
  }

  return (
    Number(property.Bedrooms) >= filters.bedsMin &&
    Number(property.FullBaths) >= filters.minBaths &&
    Number(property.FullBaths) <= filters.maxBaths &&
    Number(property.Price) >= filters.minPrice &&
    Number(property.Price) <= filters.maxPrice
  );
}

export function matchesCommercialFilters(
  property: PropertyRecord,
  residentialFilters: ResidentialFilterState,
  commercialFilters: CommercialFilterState,
): boolean {
  return (
    Number(property.Price) >= residentialFilters.minPrice &&
    Number(property.Price) <= residentialFilters.maxPrice &&
    Number(property.Area) >= commercialFilters.sizeMin &&
    Number(property.Area) <= commercialFilters.sizeMax
  );
}

export function matchesLandFilters(
  property: PropertyRecord,
  residentialFilters: ResidentialFilterState,
  landFilters: LandFilterState,
): boolean {
  return (
    Number(property.Price) >= residentialFilters.minPrice &&
    Number(property.Price) <= residentialFilters.maxPrice &&
    Number(property.Area) >= landFilters.areaMin &&
    Number(property.Area) <= landFilters.areaMax
  );
}

export function matchesSearchFilter(
  property: PropertyRecord,
  searchText: string,
): boolean {
  const normalizedSearch = normalizeSearchText(searchText);
  if (!normalizedSearch) {
    return true;
  }

  const listingId = String(property.ListingID ?? '').trim();
  if (listingId && listingId.toLowerCase() === normalizedSearch.toLowerCase()) {
    return true;
  }

  const addr = property.Address;
  if (!addr) {
    return listingId.toLowerCase().includes(normalizedSearch.toLowerCase());
  }

  const address = `${addr.Number ?? ''} ${addr.Street ?? ''}, ${addr.City ?? ''}, ${addr.State ?? ''} ${addr.Zip ?? ''} ${listingId}`
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  return address.includes(normalizedSearch.toLowerCase());
}

function normalizeSearchText(searchText: string): string {
  const trimmed = String(searchText ?? '').trim();
  if (!trimmed || trimmed.toLowerCase() === 'search') {
    return '';
  }

  return trimmed
    .replace(/,\s*USA$/i, '')
    .replace(/,\s*United States$/i, '')
    .trim();
}

export function withSuffix(value: number | string): string | number {
  const numeric = Number(Number(value).toFixed());
  if (numeric >= 1_000_000) {
    return `${(numeric / 1_000_000).toFixed()}M`;
  }
  if (numeric >= 1_000) {
    const thousands = numeric / 1_000;
    return `${(thousands >= 100 ? thousands.toFixed(0) : thousands.toFixed(1)).replace(/\.0$/, '')}K`;
  }
  return value;
}

export function formatCompactPrice(value: number): string {
  return `$${withSuffix(value)}`;
}

export function formatCompactNumber(value: number): string {
  const suffix = withSuffix(value);
  return typeof suffix === 'number' ? String(suffix) : suffix;
}

export function sortPropertiesByPrice(
  properties: PropertyRecord[],
  direction: 'ascending' | 'descending',
): PropertyRecord[] {
  const sorted = [...properties].sort(
    (left, right) => Number(left.Price ?? 0) - Number(right.Price ?? 0),
  );
  return direction === 'descending' ? sorted.reverse() : sorted;
}

export const DEFAULT_MAX_PRICE = 5_000_000;

export function initResidentialFilterOptions(filters: ResidentialFilterState): void {
  filters.listOfBathMaxOptions = buildBathOptions(filters.minBaths, filters.maxBaths);
  filters.listOfBathMinOptions = filters.listOfBathMaxOptions;
}

export function buildBathOptions(from: number, to: number): number[] {
  const options: number[] = [];
  let iter = Number(from);
  while (iter < to) {
    options.push(iter);
    iter += 1;
  }
  return options;
}

export const PRICE_FILTER_VALUES: readonly number[] = [
  50_000, 75_000, 100_000, 125_000, 150_000, 175_000, 200_000, 225_000, 250_000, 275_000, 300_000,
  350_000, 400_000, 450_000, 500_000, 600_000, 700_000, 800_000, 900_000, 1_000_000, 1_250_000,
  1_500_000, 1_750_000, 2_000_000, 2_500_000, 3_000_000, 4_000_000, 5_000_000,
];

export const SQFT_FILTER_VALUES: readonly number[] = [
  500, 750, 1_000, 1_250, 1_500, 1_750, 2_000, 2_500, 3_000, 3_500, 4_000, 5_000, 7_500, 10_000,
];

export const LOT_SQFT_FILTER_VALUES: readonly number[] = [
  1_000, 2_500, 5_000, 7_500, 10_890, 21_780, 43_560, 87_120, 174_240, 435_600,
];

export const PRICE_PER_SQFT_FILTER_VALUES: readonly number[] = [
  50, 75, 100, 125, 150, 175, 200, 250, 300, 400, 500, 750, 1_000,
];

export const ACRE_FILTER_VALUES: readonly number[] = [
  0.25, 0.5, 1, 2, 5, 10, 20, 40, 100, 250, 500, 1_000,
];

export const BUILDING_SQFT_FILTER_VALUES: readonly number[] = [
  500, 1_000, 2_500, 5_000, 7_500, 10_000, 15_000, 25_000, 50_000, 75_000, 100_000,
];

export function buildYearBuiltFilterValues(currentYear = new Date().getFullYear()): number[] {
  const years: number[] = [];
  for (let year = currentYear; year >= 1900; year -= 5) {
    years.push(year);
  }
  return years;
}

export const YEAR_BUILT_FILTER_VALUES: readonly number[] = buildYearBuiltFilterValues();

export function filterMinSelectOptions(
  values: readonly number[],
  maxValue: number | null,
  maxDefault: number,
): number[] {
  const ceiling = maxValue ?? maxDefault;
  return values.filter((value) => value < ceiling);
}

export function filterMaxSelectOptions(values: readonly number[], minValue: number | null): number[] {
  const floor = minValue ?? 0;
  return values.filter((value) => value > floor);
}

export function formatSqftSelectLabel(value: number): string {
  return `${value.toLocaleString()} sqft`;
}

export function formatLotSqftSelectLabel(value: number): string {
  return `${value.toLocaleString()} sqft`;
}

export function formatPricePerSqftSelectLabel(value: number): string {
  return `$${value.toLocaleString()}`;
}

export function formatAcresSelectLabel(value: number): string {
  return `${value} ac`;
}

export function formatBuildingSqftSelectLabel(value: number): string {
  return `${value.toLocaleString()} sqft`;
}
